import type {
  Equipment,
  EquipmentImage,
} from '../../../api/active/generated/equipment';
import type {
  PackageDetail,
  PackageMediaDetail,
} from '../../../api/active/generated/package';
import type { PlanDetail } from '../../../api/active/generated/plan';
import type { LocalCatalogState } from './localCatalogState';

export const LOCAL_DEMO_IMAGE_OBJECT_PREFIX = 'cpq-local-demo-image:';
const PREVIEW_EXPIRES_AT = '2099-12-31T23:59:59.000Z';

export const LOCAL_DEMO_IMAGE_ASSETS = [
  'lighting',
  'audio',
  'camera',
  'screen',
  'console',
  'stage',
  'microphone',
  'smoke',
  'scene',
] as const;

export type LocalDemoImageAsset = (typeof LOCAL_DEMO_IMAGE_ASSETS)[number];
export type LocalDemoImagePurpose =
  | 'product_image'
  | 'cover_package_cover_image'
  | 'lighting_package_lighting_image'
  | 'plan_image'
  | 'plan_case_image';

export function localDemoImageUrl(asset: LocalDemoImageAsset): string {
  return `/.local-demo/images/${asset}.png`;
}

export function localDemoImageObjectId(
  purpose: LocalDemoImagePurpose,
  asset: LocalDemoImageAsset,
): string {
  return `${LOCAL_DEMO_IMAGE_OBJECT_PREFIX}${purpose}:${asset}`;
}

export function localDemoImageFixture(objectId: string) {
  if (!objectId.startsWith(LOCAL_DEMO_IMAGE_OBJECT_PREFIX)) return null;
  const rest = objectId.slice(LOCAL_DEMO_IMAGE_OBJECT_PREFIX.length);
  const separator = rest.indexOf(':');
  if (separator < 0) return null;
  const purpose = rest.slice(0, separator);
  const asset = rest.slice(separator + 1);
  if (
    ![
      'product_image',
      'cover_package_cover_image',
      'lighting_package_lighting_image',
      'plan_image',
      'plan_case_image',
    ].includes(purpose) ||
    !LOCAL_DEMO_IMAGE_ASSETS.includes(asset as LocalDemoImageAsset)
  )
    return null;
  return {
    objectId,
    purpose: purpose as LocalDemoImagePurpose,
    fileName: `${asset}.png`,
    mediaType: 'image/png' as const,
    byteSize: 750_000,
    previewUrl: localDemoImageUrl(asset as LocalDemoImageAsset),
    thumbnailUrl: localDemoImageUrl(asset as LocalDemoImageAsset),
  };
}

function equipmentAsset(equipment: Equipment): LocalDemoImageAsset {
  const name = equipment.name ?? '';
  if (/屏|LED\s*(屏|大屏)/i.test(name)) return 'screen';
  if (/染色灯|光束灯|追光灯|灯具/.test(name)) return 'lighting';
  if (/调音台|导播台|切换台/.test(name)) return 'console';
  if (/摄像|摄影机|相机/.test(name)) return 'camera';
  if (/麦克风|话筒/.test(name)) return 'microphone';
  if (/音箱|音响|扬声器/.test(name)) return 'audio';
  if (/烟雾|烟机|雾机/.test(name)) return 'smoke';
  if (/舞台|桁架|雷亚架/.test(name)) return 'stage';
  return 'scene';
}

function isLocalDemoEquipment(equipment: Equipment) {
  return (
    equipment.skuCode?.startsWith('LOCAL-DEMO-') === true ||
    equipment.name?.startsWith('本地演示｜') === true
  );
}

function sameEquipmentRevision(left: Equipment, right: Equipment): boolean {
  return (
    left.id === right.id &&
    left.currentRevisionId === right.currentRevisionId &&
    left.resourceVersion === right.resourceVersion &&
    (left as Equipment & { revisionNo?: string }).revisionNo ===
      (right as Equipment & { revisionNo?: string }).revisionNo
  );
}

function samePackageRevision(
  left: PackageDetail,
  right: PackageDetail,
): boolean {
  return (
    left.id === right.id &&
    left.revision.id === right.revision.id &&
    left.resourceVersion === right.resourceVersion &&
    left.revision.revisionNo === right.revision.revisionNo
  );
}

function samePlanRevision(left: PlanDetail, right: PlanDetail): boolean {
  return (
    left.planId === right.planId &&
    left.currentRevisionId === right.currentRevisionId &&
    left.resourceVersion === right.resourceVersion &&
    left.revision?.id === right.revision?.id
  );
}

function canSeedImageList<T>(
  images: T[] | undefined,
  baselineImages: T[] | undefined,
  sameBaselineRevision: boolean,
): boolean {
  if (images === undefined) return true;
  return (
    images.length === 0 &&
    sameBaselineRevision &&
    (baselineImages === undefined || baselineImages.length === 0)
  );
}

function imageMetadata(
  purpose: LocalDemoImagePurpose,
  asset: LocalDemoImageAsset,
  sortOrder = 0,
  isPrimary = true,
) {
  return {
    byteSize: 750_000,
    isPrimary,
    mediaType: 'image/png' as const,
    objectId: localDemoImageObjectId(purpose, asset),
    previewExpiresAt: PREVIEW_EXPIRES_AT,
    previewUrl: localDemoImageUrl(asset),
    sortOrder,
    thumbnailExpiresAt: PREVIEW_EXPIRES_AT,
    thumbnailUrl: localDemoImageUrl(asset),
  };
}

function demoPlanAsset(plan: PlanDetail): LocalDemoImageAsset {
  if (/舞台|婚礼/.test(plan.name ?? '')) return 'stage';
  if (/音响|音乐节/.test(plan.name ?? '')) return 'audio';
  if (/灯光/.test(plan.name ?? '')) return 'lighting';
  return 'scene';
}

function demoPackageAsset(pkg: PackageDetail): LocalDemoImageAsset {
  const name = pkg.revision.name ?? '';
  if (/音响|麦克风|扩声/.test(name)) return 'audio';
  if (/灯光/.test(name)) return 'lighting';
  if (/舞台|桁架/.test(name)) return 'stage';
  return 'scene';
}

/** Add demo previews to new demo records and unchanged source revisions only. */
export function seedLocalDemoCatalogImages<
  T extends {
    equipmentDtos: Equipment[];
    packageDtos: PackageDetail[];
    planDtos: PlanDetail[];
    equipmentRevisionDtos: Equipment[];
    packageRevisionDtos: PackageDetail[];
    planRevisionDtos: PlanDetail[];
  },
>(
  state: T,
  baseline?: Pick<
    LocalCatalogState,
    | 'equipmentDtos'
    | 'packageDtos'
    | 'planDtos'
    | 'equipmentRevisionDtos'
    | 'packageRevisionDtos'
    | 'planRevisionDtos'
  >,
): T {
  const seedEquipment = (equipment: Equipment): Equipment => {
    if (!isLocalDemoEquipment(equipment)) return equipment;
    const source = baseline?.equipmentDtos.find((item) =>
      sameEquipmentRevision(item, equipment),
    );
    const sourceRevision = baseline?.equipmentRevisionDtos.find((item) =>
      sameEquipmentRevision(item, equipment),
    );
    const original = source ?? sourceRevision;
    if (
      !canSeedImageList(
        equipment.images,
        original?.images,
        original !== undefined,
      )
    )
      return equipment;
    const asset = equipmentAsset(equipment);
    const image: EquipmentImage = {
      ...imageMetadata('product_image', asset),
      storedObjectId: localDemoImageObjectId('product_image', asset),
    };
    return { ...equipment, images: [image] };
  };
  const seedPackage = (pkg: PackageDetail): PackageDetail => {
    if (!pkg.revision.name?.startsWith('本地演示｜')) return pkg;
    const source = baseline?.packageDtos.find((item) =>
      samePackageRevision(item, pkg),
    );
    const sourceRevision = baseline?.packageRevisionDtos.find((item) =>
      samePackageRevision(item, pkg),
    );
    const original = source ?? sourceRevision;
    const asset = demoPackageAsset(pkg);
    return {
      ...pkg,
      ...(canSeedImageList(
        pkg.coverImages,
        original?.coverImages,
        original !== undefined,
      )
        ? {
            coverImages: [
              imageMetadata('cover_package_cover_image', asset),
            ] as PackageMediaDetail[],
          }
        : {}),
      ...(canSeedImageList(
        pkg.lightingImages,
        original?.lightingImages,
        original !== undefined,
      )
        ? {
            lightingImages: [
              imageMetadata('lighting_package_lighting_image', 'lighting'),
            ] as PackageMediaDetail[],
          }
        : {}),
    };
  };
  const seedPlan = (plan: PlanDetail): PlanDetail => {
    if (!plan.name?.startsWith('本地演示｜')) return plan;
    const source = baseline?.planDtos.find((item) =>
      samePlanRevision(item, plan),
    );
    const sourceRevision = baseline?.planRevisionDtos.find((item) =>
      samePlanRevision(item, plan),
    );
    const original = source ?? sourceRevision;
    const asset = demoPlanAsset(plan);
    return {
      ...plan,
      ...(canSeedImageList(
        plan.images,
        original?.images,
        original !== undefined,
      )
        ? { images: [imageMetadata('plan_image', asset)] }
        : {}),
      ...(canSeedImageList(
        plan.caseImages,
        original?.caseImages,
        original !== undefined,
      )
        ? { caseImages: [imageMetadata('plan_case_image', 'scene')] }
        : {}),
    };
  };

  return {
    ...state,
    equipmentDtos: state.equipmentDtos.map(seedEquipment),
    packageDtos: state.packageDtos.map(seedPackage),
    planDtos: state.planDtos.map(seedPlan),
    equipmentRevisionDtos: state.equipmentRevisionDtos.map(seedEquipment),
    packageRevisionDtos: state.packageRevisionDtos.map(seedPackage),
    planRevisionDtos: state.planRevisionDtos.map(seedPlan),
  };
}
